// Planned Item editor and standing/Visit row policy.
import { inject, Injector } from '@angular/core';
import { ActivityEntityName, PlannedItemLine, VisitTaskChange } from '@jms/model';
import { resolveItemFromRow } from '@workbuddy/entities/shared/items';
import { mergeEntityBoardConfig } from '@workbuddy/services/entities/merge.entity.board.config';
import type { EntityManifestBoardProfile } from '@workbuddy/services/entities/entity.manifest.surface';
import { createEmbeddedItemAddAdapter, createEmbeddedItemBoardAdd, createEmbeddedItemDeleteCommand, embeddedItemBoardProfile, loadEmbeddedItemCatalogueFacts, lockedEmbeddedItemReadonly, type EmbeddedItemProfile } from '@workbuddy/surfaces/embedded-item';
import { resolveBoardParams, type EntityBoardContext } from '@workbuddy/ui/v2/blocks/entity.board';
import { PlannedItemNameCellRendererComponent, plannedItemNameCellValue } from '@workbuddy/entities/planned.item/cell.renderers';
import { plannedItemExcludeRowCommand } from '@workbuddy/entities/planned.item/commands/planned.item.exclude.command';
import { plannedItemRemoveRowCommand } from '@workbuddy/entities/planned.item/commands/planned.item.remove.command';
import { plannedItemRestoreRowCommand } from '@workbuddy/entities/planned.item/commands/planned.item.restore.command';
import { PLANNED_ITEM_QUERY_KEY, PLANNED_ITEM_SCOPE, type PlannedItemBoardParams } from '@workbuddy/entities/planned.item/planned.item.model';
import { PlannedItemService } from '@workbuddy/entities/planned.item/services/planned.item.service';

export function plannedItemBoardProfile(_base: EntityManifestBoardProfile, page: EntityBoardContext): Partial<EntityManifestBoardProfile> {

  const editor = {
    entity: ActivityEntityName.PlannedItem,
    lineEntity: ActivityEntityName.PlannedItem,
    scope: PLANNED_ITEM_SCOPE,
    queryKey: PLANNED_ITEM_QUERY_KEY,
    fields: { constructionRule: true },
    layout: () => 'stacked',

    load: async (row, params, injector) => {
      const line = resolveItemFromRow(row, PLANNED_ITEM_SCOPE) as PlannedItemLine | null;
      if (!line?._id || !line.name) return null;

      const facts = await loadEmbeddedItemCatalogueFacts(line.name, injector);

      // `readonly` is the server's own verdict on the row (a locked Visit, a line owned elsewhere). An
      // excluded line stays visible so it can be restored, but nothing on it is editable.
      const locked = !params?.writable() || !!line.readonly || line.visitChange === VisitTaskChange.Excluded;
      return {
        line,
        uoms: facts.uoms,
        // The Job rule is a plan and template decision: a Visit shows the rule it inherits but never
        // re-decides it, and the server refuses a Visit write that carries one.
        readonly: { ...lockedEmbeddedItemReadonly(locked, facts.canOverrideDesc), constructionRule: locked || !!params?.visit },
        hide: facts.canOverrideDesc ? {} : { description: true },
      };
    },

    priceReset: 'server',
    serverZeroesForcedPrices: false,

    save: async (line, patch, _params, injector) => {
      await injector.get(PlannedItemService).update(line._id, patch);
    },

    afterWrite: params => params?.itemsChanged(),

    board: {
      rowHeight: 56,

      // The Item cell gains this Visit's change to the line as a pill beside the name. Only on a Visit
      // mount: a standing board's rows have no `visitChange`, and swapping the shared cell there would
      // buy a second renderer that renders identically. The kit's own name-cell override has already
      // run, so this replaces it rather than composing with it — the planned-item cell is the shared
      // one plus the pill, so nothing the kit decided is lost.
      overrideCellRenderer: (context, _metadata, field, renderer) => (field.name === 'name' && !!resolveBoardParams<PlannedItemBoardParams>(context)?.visit
        ? { ...renderer, cellRenderer: PlannedItemNameCellRendererComponent, valueGetter: plannedItemNameCellValue }
        : renderer),

      // Declaring `rowCommands` takes the whole row menu, so this one branch owns both shapes. On a
      // Visit, Exclude / Restore / Remove replace Delete outright: a Visit never deletes a standing
      // line, and a line it added is only deleted after it has been excluded — so removal is undoable
      // right up to the last click. Every other parent owns its lines outright and gets the kit Delete
      // built from the profile's `delete` below.
      rowCommands: (row, params) => {
        if (!params?.visit) {
          return params?.embeddedItem
            ? createEmbeddedItemDeleteCommand(params.embeddedItem, row, params)
            : [];
        }

        const line = resolveItemFromRow(row, PLANNED_ITEM_SCOPE) as PlannedItemLine | null;
        const onSaved = () => params?.itemsChanged();
        return line?._id ? [plannedItemExcludeRowCommand(line, onSaved), plannedItemRestoreRowCommand(line, onSaved), plannedItemRemoveRowCommand(line, onSaved)] : [];
      },
    },

    delete: {
      confirm: line => ({ title: 'Delete item', message: `Delete '${line.name}'?` }),
      write: async (line, _params, injector) => {
        await injector.get(PlannedItemService).delete(line._id);
      },
    },
  } satisfies EmbeddedItemProfile<PlannedItemLine, PlannedItemBoardParams>;

  const entityBoard = embeddedItemBoardProfile(editor);

  const params = page.params as PlannedItemBoardParams;
  const injector = inject(Injector);
  const items = inject(PlannedItemService);
  if (!params?.parent) return entityBoard;

  const mountParams: PlannedItemBoardParams = {
    ...params,
    itemBrowser: () => createEmbeddedItemAddAdapter({
      title: 'Browse Items',
      canAdd: () => !!params.parent() && params.writable(),
      write: addLines => {
        const parent = params.parent();
        if (!parent) throw new Error(params.requiresParentMessage ?? 'A parent is required to add items.');

        return items.add(parent, addLines, params.visit?.()?._id);
      },
      queryKey: PLANNED_ITEM_QUERY_KEY,
    }, injector),
  };

  return mergeEntityBoardConfig(entityBoard, {
    params: mountParams,
    header: {
      onAdd: createEmbeddedItemBoardAdd(editor, mountParams, injector),
    },
  }) as Partial<EntityManifestBoardProfile>;
}
